import { GlobalStore, GridStore } from "@roc/feature-app-core";
import { GENERIC_ERROR_MESSAGE } from "@roc/feature-utils";
import { action, flow, makeObservable, observable } from "mobx";
import { LeadService } from "../../services/leadService";
import { EditLeadNoteStore } from "./editLeadNoteStore";
import { EditLeadActivityStore } from "./editLeadActivityStore";
import { EditLeadTaskStore } from "./editLeadTaskStore";
import { RecordHistoryStore } from "./recordHistoryStore";
import { EditRealEstateTransactionStore } from "./editRealEstateTransactionsStore";
import { LEAD_OBJECT_TYPE } from "../../components/leads/constants/constants";

export class LeadDetailsStore {
  private globalStore: GlobalStore;
  public leadService: LeadService;
  editLeadNoteStore: EditLeadNoteStore;
  editLeadActivityStore: EditLeadActivityStore;
  editLeadTaskStore: EditLeadTaskStore;
  recordHistoryStore: RecordHistoryStore;
  editRealEstateTransactionStore: EditRealEstateTransactionStore;



  leadNotes = [];
  leadActivities = [];
  leadTasks = [];
  loanVolumeStats = {}
  additionalPhones = [];
  additionalEmails = [];
  realEstateTransactions = [];
  objectId = null;
  type = null;
  dncPrimary = null;
  dncSecondary = null;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.leadService = new LeadService();
    this.editLeadNoteStore = new EditLeadNoteStore(globalStore, this);
    this.editLeadActivityStore = new EditLeadActivityStore(globalStore, this);
    this.editLeadTaskStore = new EditLeadTaskStore(globalStore, this);
    this.recordHistoryStore = new RecordHistoryStore(globalStore, this);
    this.editRealEstateTransactionStore = new EditRealEstateTransactionStore(globalStore, this);
    makeObservable(this, {
      leadNotes: observable,
      leadActivities: observable,
      leadTasks: observable,
      loanVolumeStats: observable,
      additionalEmails: observable,
      additionalPhones: observable,
      objectId: observable,
      type: observable,
      realEstateTransactions: observable,
      fetchLeadDetails: flow,
      fetchLeadNotes: flow,
      fetchLeadActivities: flow,
      fetchLeaDncDetails: flow,
      dncPrimary: observable,
      dncSecondary: observable,
      resetDnc: action,
      fetchLeadTasks: flow,
      fetchLoanVolumeStats: flow,
      fetchAdditionalContactInfo: flow,
      deleteAdditionalContactData: flow,
      fetchRealEstateTransactions: flow,
    })
  }

  reset() {
    this.leadNotes = [];
    this.leadActivities = [];
    this.leadTasks = [];
    this.objectId = null;
    this.type = null;
    this.loanVolumeStats = {};
    this.realEstateTransactions = [];
    this.dncPrimary = null;
    this.dncSecondary = null;
    this.resetAdditionalData();
  }

  resetDnc() {
    this.dncPrimary = null;
    this.dncSecondary = null;
  }

  resetAdditionalData() {
    this.additionalEmails = [];
    this.additionalPhones = [];
  }

  initialize(objectId: number, type: string) {
    this.reset();
    this.objectId = objectId;
    this.type = type;
  }

  *fetchLeadDetails(objectId: number, type: string, businessUnit: string) {
    try {
      this.initialize(objectId, type);
      yield this.fetchLeadNotes(businessUnit);
      yield this.fetchLeadActivities(businessUnit);
      yield this.fetchLeadTasks(businessUnit);
      yield this.fetchAdditionalContactInfo();
      yield this.fetchRealEstateTransactions(true);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE
      })
    }
  }

  *fetchLeaDncDetails(objectId: number, type: string, primaryPhone?: string, secondaryPhone?: string) {
    try {
      if (primaryPhone) {
        const primaryDnc = yield this.leadService.getDncDataByPhone(primaryPhone);
        this.dncPrimary = primaryDnc?.data?.data || primaryDnc?.data || primaryDnc;
      }
      if (secondaryPhone) {
        const secondaryDnc = yield this.leadService.getDncDataByPhone(secondaryPhone);
        this.dncSecondary = secondaryDnc?.data?.data || secondaryDnc?.data || secondaryDnc;
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE
      })
    }
  }

  *fetchAdditionalContactInfo() {
    try {
      const response = yield this.leadService.getLeadAdditionalContactInfo(this.objectId);
      const additionalContactInfo = response.data.data;
      additionalContactInfo.forEach(contact => {
        if (/@/.test(contact.data)) {
          this.additionalEmails.push(contact.data);
        } else {
          this.additionalPhones.push(contact.data);
        }
      });
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE
      })
    }
  }

  *fetchLeadNotes(businessUnit: string, leadId?: string) {
    try {
      const targetId = this.objectId ?? leadId;
      const response = yield this.leadService.getLeadNotes(targetId, this.type, businessUnit);
      this.leadNotes = response.data.data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *fetchLeadActivities(businessUnit: string, leadId?: string) {
    try {
      const targetId = this.objectId ?? leadId;
      const response = yield this.leadService.getLeadActivities(targetId, this.type, businessUnit);
      this.leadActivities = response.data.data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE
      })
    }
  }

  *fetchLeadTasks(businessUnit: string, leadId?: string) {
    try {
      const targetId = this.objectId ?? leadId;
      const response = yield this.leadService.getLeadTasks(targetId, this.type, businessUnit);
      this.leadTasks = response.data.data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE
      })
    }
  }

  *fetchLoanVolumeStats(id: number, type: string) {
    try {
      const response = yield this.leadService.getLoanVolumeStats(id, type);
      this.loanVolumeStats = response.data.data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE
      })
    }
  }

  *deleteAdditionalContactData(value, reason) {
    try {
      const body = {
        data: value,
        reason: reason,
        leadId: this.objectId
      };
      yield this.leadService.deleteLeadAdditionalContactInfo(body);
      this.resetAdditionalData();
      this.fetchAdditionalContactInfo();
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE
      })
    }
  }

  *fetchRealEstateTransactions(disableGloablLoading = false) {
    try {
      // objectId is a contactId on the contact screen, so the by-lead endpoint must not be used there.
      const response = yield this.type === LEAD_OBJECT_TYPE
        ? this.leadService.getRealEstateTransactions(this.objectId, disableGloablLoading)
        : this.leadService.getRealEstateTransactionsByContactId(this.objectId, disableGloablLoading);
      this.realEstateTransactions = response.data.data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE
      })
    }
  }
}
